import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import {
  Instagram,
  MessageCircle,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Trash2,
  Share2
} from 'lucide-react';
import { useUser } from '../providers/UserProvider';
import { sendRequest } from '../api';
import { initiateInstagramOAuth } from '../utils/instagram';

const ConnectedAccountsPage = ({ onError }) => {
  const { user, fetchUser } = useUser();
  const [connecting, setConnecting] = useState(false);
  const [platformToDisconnect, setPlatformToDisconnect] = useState(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [error, setError] = useState(null);

  const handleConnect = async (platform) => {
    setConnecting(true);
    setError(null); // Clear any previous errors
    try {
      if (platform === 'instagram') {
        await new Promise(resolve => {
          initiateInstagramOAuth();
          resolve();
        });
      } else if (platform === 'tiktok') {
        window.location.href = `${process.env.API_BASE_URL}/auth/tiktok?disable_auto_auth=1`;
      }
    } catch (error) {
      console.error('Failed to connect account:', error);
      setError(error.message || 'Failed to connect account. Please try again.');
      onError?.(error);
    } finally {
      setConnecting(false);
    }
  };

  const getConnectedPlatforms = (platformName) => {
    const platforms = user?.platforms?.filter(p => p.name === platformName) || [];
    return platforms.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  };

  const isPrimaryTikTokAccount = (account) => {
    return account.name === 'tiktok' && account.username === user?.username;
  };

  const handleDisconnect = (platform) => {
    // Prevent disconnecting primary TikTok account
    if (isPrimaryTikTokAccount(platform)) {
      setError("Cannot remove primary TikTok account");
      return;
    }
    setPlatformToDisconnect(platform);
  };

  const sortPlatforms = (platforms) => {
    return platforms?.sort((a, b) => {
      // Primary TikTok account always first
      if (isPrimaryTikTokAccount(a)) return -1;
      if (isPrimaryTikTokAccount(b)) return 1;
      // Then sort by created_at
      return new Date(a.created_at) - new Date(b.created_at);
    });
  };

  const confirmDisconnect = async () => {
    if (isDisconnecting) return;
    
    setIsDisconnecting(true);
    try {
      await sendRequest(`/auth/${platformToDisconnect.name}`, 'DELETE', {
        uid: platformToDisconnect.uid
      });
      console.log('Disconnected account:', platformToDisconnect.name);
      await fetchUser();
      setPlatformToDisconnect(null);
    } catch (error) {
      console.error('Failed to disconnect account:', error);
      onError?.(error);
      setPlatformToDisconnect(null);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const socialPlatforms = [
    {
      id: 'instagram',
      name: 'Instagram',
      icon: <Instagram className="h-6 w-6" />,
      color: 'from-purple-500 to-pink-500',
      description: 'Share your content directly to Instagram and reach your audience where they are.'
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.7203 1.25H9.28025V9.5C9.28025 10.4665 8.49675 11.25 7.53025 11.25C6.56375 11.25 5.78025 10.4665 5.78025 9.5C5.78025 8.5335 6.56375 7.75 7.53025 7.75C7.58225 7.75 7.63325 7.7535 7.68425 7.7585V5.2915C7.63325 5.289 7.58225 5.2865 7.53025 5.2865C5.20525 5.2865 3.31525 7.1765 3.31525 9.5015C3.31525 11.8265 5.20525 13.7165 7.53025 13.7165C9.85525 13.7165 11.7453 11.8265 11.7453 9.5015V5.4465C12.5853 6.0415 13.5903 6.4115 14.6653 6.4115V3.948C13.0353 3.948 11.7203 2.7665 11.7203 1.25Z"
            fill="currentColor"
          />
        </svg>
      ),
      color: 'from-black to-gray-800',
      description: 'Connect with TikTok to share your content with a growing audience.'
    }
  ];

  console.log(socialPlatforms);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950">
      <div className="container mx-auto px-8 py-8 pt-[calc(var(--navbar-height)_+_1rem)]">
        {/* Hero Section */}
        <div className="mb-8 bg-gray-900/50 rounded-2xl p-8 shadow-lg border border-gray-800">
          <div className="flex flex-col justify-center space-y-6">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 text-transparent bg-clip-text">
              Connected Accounts
            </h1>
            <p className="text-gray-400 text-lg">
              Connect multiple social media accounts to enable cross-posting and automation features
            </p>
          </div>
        </div>

        {/* Connect New Account Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full mb-8">
          {socialPlatforms.map((platform) => (
            <div
              key={platform.id}
              className="bg-gray-900/50 rounded-2xl shadow-lg border border-gray-800 p-6 transition-all duration-300 hover:shadow-xl hover:border-gray-700 w-full"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className={`p-3 rounded-xl bg-gradient-to-r ${platform.color} text-white shrink-0`}>
                  {platform.icon}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-100">
                    Connect {platform.name}
                  </h2>
                </div>
              </div>

              <p className="text-gray-400 mb-6">
                {platform.description}
              </p>

              <Button
                onClick={() => handleConnect(platform.id)}
                disabled={connecting}
                className={`w-full justify-center bg-gradient-to-r ${platform.color} text-white hover:opacity-90 shadow-lg shadow-${platform.color}/25 rounded-xl h-12`}
              >
                {connecting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  `Add ${platform.name} Account`
                )}
              </Button>
              
              {error && (
                <div className="mt-4 p-3 bg-red-950/50 border border-red-900 rounded-xl">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Connected Accounts List */}
        <div className="bg-gray-900/50 rounded-2xl shadow-lg border border-gray-800 p-6">
          <h2 className="text-2xl font-semibold text-gray-100 mb-6">Connected Accounts</h2>
          <div className="space-y-4">
            {sortPlatforms(user?.platforms)
              ?.map((account) => (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-4 bg-gray-800/50 rounded-xl border border-gray-700"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-[6px] bg-gradient-to-r ${socialPlatforms.find(p => p.id === account.name).color} text-white`}>
                      {socialPlatforms.find(p => p.id === account.name).icon}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-100">{socialPlatforms.find(p => p.id === account.name).name}</h3>
                      <p className="text-gray-400">@{account.username}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {account.last_synced_at && (
                      <p className="text-xs text-gray-500">
                        Last synced: {new Date(account.last_synced_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                          hour12: true
                        })}
                      </p>
                    )}
                    {!isPrimaryTikTokAccount(account) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDisconnect(account)}
                        className="text-gray-400 hover:text-red-400 w-9 h-9 rounded-xl"
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            {!user?.platforms?.length && (
              <p className="text-gray-400 text-center py-4">No accounts connected yet</p>
            )}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mt-8 bg-gradient-to-r from-indigo-950/50 to-purple-950/50 rounded-2xl p-8 border border-gray-800 shadow-lg">
          <h3 className="text-xl font-semibold text-gray-100 mb-6">
            Why connect your accounts?
          </h3>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 hover:border-gray-700 transition-colors">
              <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
                <Share2 className="h-6 w-6" />
              </div>
              <h4 className="text-lg font-medium text-gray-100 mb-2">Cross-Platform Posting</h4>
              <p className="text-gray-400">Enable seamless content sharing across multiple platforms</p>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 hover:border-gray-700 transition-colors">
              <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                <Share2 className="h-6 w-6" />
              </div>
              <h4 className="text-lg font-medium text-gray-100 mb-2">Automated Distribution</h4>
              <p className="text-gray-400">Set up automated content distribution schedules</p>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 hover:border-gray-700 transition-colors">
              <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 text-white">
                <Share2 className="h-6 w-6" />
              </div>
              <h4 className="text-lg font-medium text-gray-100 mb-2">Centralized Management</h4>
              <p className="text-gray-400">Manage all your social media presence from one place</p>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {platformToDisconnect && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-gray-900 rounded-2xl p-8 max-w-md w-full mx-4 shadow-xl border border-gray-800">
              <h3 className="text-xl font-semibold text-gray-100 mb-6">
                Disconnect Account
              </h3>
              <p className="text-gray-400 mb-8">
                Are you sure you want to disconnect @{platformToDisconnect.username} from {platformToDisconnect.name}? This action cannot be undone.
              </p>

              <div className="flex gap-4 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setPlatformToDisconnect(null)}
                  className="rounded-xl border-gray-700 hover:bg-gray-800 text-gray-300"
                  disabled={isDisconnecting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDisconnect}
                  className="bg-red-500/80 hover:bg-red-600/80 text-white rounded-xl"
                  disabled={isDisconnecting}
                >
                  {isDisconnecting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Disconnecting...
                    </>
                  ) : (
                    'Disconnect Account'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectedAccountsPage; 